import { Icon, TIconName } from '@treezor/design-system';
import { useTranslation } from 'react-i18next';

import {
  getAuthorizationStatus,
  authorizationMti,
  ITransactionByDigitizedCard,
  TAuthorizationStatus,
} from '@/features/cards/store/models/card.model';
import {
  getCardTransactionConfigKey,
  TCardTransactionColumnSetting,
} from '@/features/cards/store/models/card-transactions.model';
import { PaymentTruncateCell } from '@/features/cards/components/CardTransactionsTable/PaymentTruncateCell';

interface IPaymentAuthorizationCellProps {
  transaction: ITransactionByDigitizedCard;
  config?: TCardTransactionColumnSetting;
  shouldTrancate?: boolean;
}

const authorizationIconColorMap: Record<TAuthorizationStatus, { icon: TIconName; className?: string }> = {
  APPROVE: { icon: 'check_circle', className: 'text-icon-success' },
  CALL_ISSUER: { icon: 'call_quality' },
  CAPTURE: { icon: 'lock' },
  DECLINE: { icon: 'cancel', className: 'text-icon-error' },
};

export function PaymentAuthorizationCell({
  transaction,
  config,
  shouldTrancate = true,
}: IPaymentAuthorizationCellProps) {
  const { t } = useTranslation();

  const reponseCode = getAuthorizationStatus(transaction?.authorizationResponseCode);
  const textProps = authorizationIconColorMap[reponseCode];

  const responseDescription = reponseCode
    ? [
        transaction.authorizationResponseCode,
        t(
          `pages.currentProfiles.cards.transactions.table.headers.authorizationStatus.${transaction.authorizationResponseCode}`,
        ),
      ].join(': ')
    : transaction?.authorizationResponseCode;

  return (
    <div className="flex flex-col gap-1">
      {getCardTransactionConfigKey(config, 'authorizationResponseCode') && transaction?.authorizationResponseCode && (
        <div className="flex items-center text-secondary typo-body-regular-small text-nowrap gap-1">
          {reponseCode ? (
            <>
              <Icon name={textProps?.icon} size="s" className={textProps?.className} />
              <div className="flex items-center justify-start">
                {shouldTrancate ? (
                  <PaymentTruncateCell textString={responseDescription} />
                ) : (
                  <span className="typo-body-regular-paragraph text-secondary">{responseDescription}</span>
                )}
              </div>
            </>
          ) : (
            transaction.authorizationResponseCode
          )}
        </div>
      )}
      {getCardTransactionConfigKey(config, 'authorizationMti') && transaction?.authorizationMti && (
        <div className="flex items-center text-nowrap text-tertiary typo-body-regular-small">
          {authorizationMti[transaction.authorizationMti] ? (
            <span>
              {[
                transaction.authorizationMti,
                t(
                  `pages.currentProfiles.cards.transactions.table.headers.authorizationMtiStatus.${authorizationMti[transaction.authorizationMti]}`,
                ),
              ].join(': ')}
            </span>
          ) : (
            transaction.authorizationMti
          )}
        </div>
      )}
    </div>
  );
}
